/**
 * PassportStages — SIGNUP-3: Passport inside setup, from the redesign.
 *
 *   Meet    — "A goal is a planet": one world with two rings (one
 *             full, one half), Nova curious beside it.
 *   Compose — "What are you chasing?": three goal types as chips; the
 *             world forms as one is picked; the number and the name
 *             are capsules.
 *   World   — "Your first world": the planet drifts from the composer
 *             to the outer orbit of the studio's galaxy, plays on load.
 *
 * Planets are the app's own PlanetView (components/passport), so the
 * world a studio sets here is the one they meet on Today and in the
 * Galaxy. The numbers are the wizard's; this file only shows them.
 *
 * PASSPORT-2 — the goal is one sentence with one counter ring
 * (bookings, paid revenue or galleries delivered). The server picks
 * the world; the World stage shows the one it chose.
 */
import { useEffect, useRef, useState } from 'react';
import { Nova } from '@/components/nova';
import { PlanetView } from '@/components/passport/planet-view';
import type { Goal, SubGoalType } from '@/components/passport/galaxy-core';
import type { CounterMetric } from '@/services/passport';
import type { WorldDef } from '@/components/galaxy3d/worlds';
import { WORLD_SKIN } from '@/components/galaxy3d/worlds';
import { FirstRunFrame, StageBlock, StageCapsule, StageChip, StageCTA, StageHint, StageRow, StageText } from '@/components/firstrun/Stage';
import { enterStage, swapStage } from '@/components/firstrun/stageMotion';
import { GOALS, IC, PASSPORT_GOLD, type GoalKey } from '@/components/firstrun/tokens';
import type { StepNav } from './FirstRunSteps';

const RING_TYPE: Record<GoalKey, SubGoalType> = { bookings: 'bookings', revenue: 'revenue', galleries: 'craft' };

function goalFor(key: GoalKey, rings: number[], name?: string): Goal {
  const g = GOALS[key];
  return {
    id: `setup-${key}`,
    title: name || g.sug,
    worldName: g.world,
    skin: g.skin,
    state: 'active',
    subGoals: rings.map((p, i) => ({ id: `r${i}`, title: g.label, type: i === 0 ? RING_TYPE[key] : 'revenue', progress: p })),
  };
}

const SUN = 'radial-gradient(circle at 45% 40%,#FFF1B8 0%,#F2D24E 45%,#C9A02E 100%)';

export function goalText(metric: CounterMetric, target: string) {
  const n = target || GOALS[metric as GoalKey]?.def || '';
  if (metric === 'revenue') return `$${n} paid`;
  if (metric === 'galleries') return `${n} galleries delivered`;
  if (metric === 'clients') return `${n} new clients`;
  return `${n} booked`;
}

// ── p1 Meet Passport ───────────────────────────────────────────────

export function PassportMeetStage({ nav }: { nav: StepNav }) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => { enterStage(rootRef.current, 1); }, []);
  return (
    <FirstRunFrame
      rootRef={rootRef}
      palette="space"
      accent={PASSPORT_GOLD}
      onBack={nav.onBack ? () => swapStage(rootRef.current, -1, nav.onBack!) : undefined}
      right={nav.onSkipAll ? { label: 'Skip setup', onClick: nav.onSkipAll } : undefined}
      stage={
        <>
          <StageRow dx={8}><StageChip label="Bookings · 12 of 12" selected accent={PASSPORT_GOLD} index={0} /></StageRow>
          <StageBlock kind="cap">
            <div className="relative my-1.5 w-fit" style={{ animation: 'frHaloPulse 5s ease-in-out infinite' }}>
              <PlanetView goal={goalFor('bookings', [1, 0.48])} diameter={170} />
              <div className="absolute -right-[4%] top-[6%]" style={{ animation: 'frNovaBob 3.4s ease-in-out infinite' }}>
                <Nova state="curious" size={56} />
              </div>
            </div>
          </StageBlock>
          <StageRow dx={-8}><StageChip label="Revenue · $24,000 of $50,000" accent={PASSPORT_GOLD} index={1} /></StageRow>
          <StageHint>It fills by itself, from real work. Nothing to tick off.</StageHint>
        </>
      }
      text={
        <StageText count="Passport" icon={IC.planet} accent={PASSPORT_GOLD}
          title="A goal is a planet."
          body="Set one and a new world appears at the edge of your galaxy. It fills from real work — bookings, paid invoices, galleries out the door. When it’s done, Nova flies out and stamps your Passport." />
      }
      cta={<StageCTA primary="Set your first goal" onPrimary={() => swapStage(rootRef.current, 1, nav.onNext)} skip="Skip for now" onSkip={() => swapStage(rootRef.current, 1, nav.onSkip ?? nav.onNext)} />}
    />
  );
}

// ── p2 Compose ─────────────────────────────────────────────────────

export function PassportComposeStage({ nav, metric, target, name, onMetric, onTarget, onName, picked }: {
  nav: StepNav; metric: CounterMetric; target: string; name: string;
  onMetric: (m: CounterMetric) => void; onTarget: (v: string) => void; onName: (v: string) => void;
  /** False until the studio has chosen a type on this visit. */
  picked: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => { enterStage(rootRef.current, 1); }, []);
  const key = (metric as GoalKey) in GOALS ? (metric as GoalKey) : 'bookings';
  const G = GOALS[key];
  const swatch: Record<GoalKey, string> = { bookings: '#D4541F', revenue: '#D4A02C', galleries: '#2D6E70' };
  return (
    <FirstRunFrame
      rootRef={rootRef}
      palette="space"
      accent={PASSPORT_GOLD}
      onBack={nav.onBack ? () => swapStage(rootRef.current, -1, nav.onBack!) : undefined}
      right={nav.onSkipAll ? { label: 'Skip setup', onClick: nav.onSkipAll } : undefined}
      stage={
        <>
          <StageRow>
            {(Object.keys(GOALS) as GoalKey[]).map((id, i) => (
              <StageChip key={id} label={GOALS[id].label} big swatch={swatch[id]} selected={picked && key === id} accent={swatch[id]} noCheck onClick={() => onMetric(id)} index={i} />
            ))}
          </StageRow>
          <StageHint>{picked ? `Suggested: ${G.hint}` : 'Bookings · Revenue · Galleries'}</StageHint>
          <StageBlock kind="cap">
            <div className="relative my-1.5 w-fit">
              {picked ? (
                <div style={{ animation: 'frHaloPulse 5s ease-in-out infinite' }}>
                  <PlanetView goal={goalFor(key, [0], name)} diameter={140} />
                </div>
              ) : (
                <div className="grid h-[180px] w-[180px] place-items-center rounded-full border-[1.5px] border-dashed border-[#F5F3EF]/30 p-6 text-center text-[14px] leading-[1.45] text-[#F5F3EF]/60">Your world appears here</div>
              )}
            </div>
            {picked && (
              <div className="mt-1.5 flex flex-col gap-1 text-center">
                <span className="text-[11px] font-semibold uppercase tracking-[.22em]" style={{ color: PASSPORT_GOLD }}>{G.world}</span>
                <span className="font-editorial italic leading-[1.1]" style={{ fontSize: 'clamp(20px, 2vw, 28px)', color: name ? '#F5F3EF' : 'rgba(245,243,239,.45)' }}>{name || G.sug}</span>
                <span className="text-[14px] text-[#F5F3EF]/70">{goalText(key, target)}</span>
              </div>
            )}
          </StageBlock>
          {picked && (
            <>
              <StageCapsule value={target} editable big focus accent={PASSPORT_GOLD} onChange={(v) => onTarget(v.replace(/[^0-9,]/g, ''))} placeholder={G.def} inputType="text" />
              <StageCapsule label="Call it" value={name} editable onChange={onName} placeholder={G.sug} accent={PASSPORT_GOLD} />
            </>
          )}
        </>
      }
      text={
        <StageText count="Optional" label="Passport" icon={IC.flag} accent={PASSPORT_GOLD}
          title="What are you chasing?"
          body="One sentence. Pick what counts, and Orbit tracks it from real work." />
      }
      cta={<StageCTA primary="Make it a world" disabled={!picked} loading={nav.busy} onPrimary={() => swapStage(rootRef.current, 1, nav.onNext)} skip="Skip for now" onSkip={() => swapStage(rootRef.current, 1, nav.onSkip ?? nav.onNext)} />}
    />
  );
}

// ── p3 Your first world ────────────────────────────────────────────

export function PassportWorldStage({ nav, metric, name, studioName, world }: { nav: StepNav; metric: CounterMetric; name: string; studioName: string; /** the world the server charted the goal on */ world?: WorldDef | null }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    enterStage(rootRef.current, 1);
    const a = setTimeout(() => setFrame(1), 700);
    const b = setTimeout(() => setFrame(2), 2300);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, []);
  const key = (metric as GoalKey) in GOALS ? (metric as GoalKey) : 'bookings';
  const G = GOALS[key];
  const worldName = world?.name ?? G.world;
  const shown = world ? { ...goalFor(key, [0], name), worldName, skin: WORLD_SKIN[world.type] } : goalFor(key, [0], name);
  // frame 0: big, centred over the sun; 1: shrinking toward the edge; 2: settled on the outer orbit
  const px = ['50%', '76%', '88%'][frame];
  const py = ['50%', '32%', '22%'][frame];
  const sc = [1, 0.5, 0.36][frame];
  const ease = 'cubic-bezier(.2,.8,.2,1)';
  return (
    <FirstRunFrame
      rootRef={rootRef}
      palette="space"
      accent={PASSPORT_GOLD}
      right={nav.onSkipAll ? { label: 'Skip setup', onClick: nav.onSkipAll } : undefined}
      stage={
        <StageBlock>
          <div className="relative w-full" style={{ maxWidth: 'min(540px, 50vw)', aspectRatio: '1' }}>
            {[62, 78, 92].map((d, i) => (
              <div key={d} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: `${d}%`, height: `${d}%`, boxShadow: `inset 0 0 0 1px rgba(245,243,239,${0.16 - i * 0.03})` }} />
            ))}
            <div className="absolute left-1/2 top-1/2 aspect-square w-[12%] -translate-x-1/2 -translate-y-1/2 rounded-full transition-opacity duration-1000" style={{ background: SUN, boxShadow: '0 0 40px rgba(242,210,78,.55),0 0 120px rgba(242,210,78,.25)', opacity: frame > 0 ? 1 : 0 }} />
            <div className="absolute left-1/2 top-[58%] -translate-x-1/2 whitespace-nowrap text-[10px] uppercase tracking-[.2em] text-[#F5F3EF]/50 transition-opacity duration-1000" style={{ opacity: frame > 0 ? 1 : 0 }}>{studioName || 'Your studio'}</div>
            {frame === 2 && (
              <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" fill="none" aria-hidden>
                <line x1="50" y1="50" x2="88" y2="22" stroke="rgba(188,148,47,.7)" strokeWidth=".35" strokeDasharray="1.2 1.6" />
              </svg>
            )}
            <div className="absolute" style={{ left: px, top: py, transform: `translate(-50%,-50%) scale(${sc})`, transition: `left 1.6s ${ease}, top 1.6s ${ease}, transform 1.6s ${ease}` }}>
              <div style={{ animation: 'frHaloPulse 5s ease-in-out infinite' }}>
                <PlanetView goal={shown} diameter={180} compact />
              </div>
              <div className="absolute left-1/2 top-full mt-2 flex -translate-x-1/2 flex-col items-center gap-0.5 transition-opacity duration-700" style={{ opacity: frame === 2 ? 1 : 0 }}>
                <span className="whitespace-nowrap text-[28px] font-semibold uppercase tracking-[.2em]" style={{ color: PASSPORT_GOLD }}>{worldName}</span>
                <span className="whitespace-nowrap text-[30px] text-[#F5F3EF]/70">{name || G.sug}</span>
              </div>
            </div>
            <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: frame === 2 ? '78%' : '60%', top: frame === 2 ? '32%' : '64%', transition: 'left 1.5s cubic-bezier(.4,0,.3,1), top 1.5s cubic-bezier(.4,0,.3,1)' }}>
              <div style={{ animation: 'frNovaBob 3.4s ease-in-out infinite' }}><Nova state="curious" size={48} /></div>
            </div>
          </div>
        </StageBlock>
      }
      text={
        <StageText count="Your first world" icon={IC.planet} accent={PASSPORT_GOLD}
          title={`${worldName} is out there.`}
          body={`Every ${G.unit} brings you closer.`} />
      }
      cta={<StageCTA primary="Continue" onPrimary={() => swapStage(rootRef.current, 1, nav.onNext)} />}
    />
  );
}
